import React, { useState, FormEvent, ChangeEvent, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from 'components/Header/Navbar';
import Button from 'components/Button';
import ReelUpload from 'components/reels/ReelUpload';
import ReelUploadModal from 'components/reels/ReelUploadModal';
import { listingService } from 'api/services/ListingService';
import { topCategoryService } from 'api/services/TopCategoryService';
import { conditionService } from 'api/services/ConditionService';
import { warrantyService } from 'api/services/WarrantyService';
import { productTypeService } from 'api/services/ProductTypeService';
import { cityService } from 'api/services/CityService';
import { districtService } from 'api/services/DistrictService';
import { sortCities } from 'utils/sortCities';
import type { TopCategoryViewModel } from 'api/types/top-category';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import type { ConditionViewModel } from 'api/types/condition';
import type { WarrantyViewModel } from 'api/types/warranty';
import {
  SpecConstraintViewModel,
} from 'api/types/product-type/SpecConstraintViewModel';
import SlidingCategoryNavigator from 'components/Filter/SlidingCategoryNavigator';
import SpecInlineFilterPanel from 'components/Filter/SpecInlineFilterPanel';
import type { ListingFilterState } from 'types/listingFilterState';
import { defaultListingFilterState } from 'types/listingFilterState';
import { normalizeSpecConstraintList } from 'utils/specConstraintUtils';
import { isListingImageFile } from 'utils/imageFileUtils';

interface FormData {
  category: string;
  secondaryCategoryId: string;
  productTypeId: string;
  brand: string;
  cpuModel: string;
  ram: string;
  storage: string;
  gpu: string;
  price: string;
  title: string;
  description: string;
  condition: string;
  warranty: string;
  testedSystem: boolean;
  city: string;
  district: string;
  neighborhood: string;
  termsAccepted: boolean;
  images: FileList | null;
  showPhoneOnListing: boolean;
}


const Ilan_ver: React.FC = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [topCategories, setTopCategories] = useState<TopCategoryViewModel[]>([]);
  const [conditions, setConditions] = useState<ConditionViewModel[]>([]);
  const [warranties, setWarranties] = useState<WarrantyViewModel[]>([]);
  const [specConstraints, setSpecConstraints] = useState<SpecConstraintViewModel[]>([]);
  const [specValues, setSpecValues] = useState<Record<string, string>>({});
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const [userForm, setFormData] = useState<FormData>({
    category: '',
    secondaryCategoryId: '',
    productTypeId: '',
    brand: '',
    cpuModel: '',
    ram: '',
    storage: '',
    gpu: '',
    price: '',
    title: '',
    description: '',
    condition: '',
    warranty: '',
    testedSystem: false,
    city: '',
    district: '',
    neighborhood: '',
    termsAccepted: false,
    images: null,
    showPhoneOnListing: false,
  });

  // Feature 009 (US2): optional reel video uploaded together with the listing.
  const [reelFile, setReelFile] = useState<File | null>(null);
  const [pendingReelFile, setPendingReelFile] = useState<File | null>(null);
  const [showReelModal, setShowReelModal] = useState(false);

  // Collapse state for property sections (Feature 014: US3)
  const [propertySections, setPropertySections] = useState<Record<string, boolean>>({
    categories: true,
    specs: true,
    condition: true,
    warranty: true,
  });

  const togglePropertySection = (sectionKey: string) => {
    setPropertySections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const handleChange = (e: ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({
        ...prev,
        images: e.target.files
      }));
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [tcRes, condRes, warrRes] = await Promise.all([
          topCategoryService.getPopulated(),
          conditionService.getAll(),
          warrantyService.getAll(),
        ]);
        if (tcRes.isSuccess && tcRes.data) setTopCategories(tcRes.data);
        if (condRes.isSuccess && condRes.data) setConditions(condRes.data);
        if (warrRes.isSuccess && warrRes.data) setWarranties(warrRes.data);
      } catch {
        // Fallback: keep empty; dropdowns will show no options or static fallback
      }
    };
    load();
  }, []);

  useEffect(() => {
    cityService.getAll().then((res) => {
      if (res.isSuccess && res.data) setCities(sortCities(res.data));
    });
  }, []);

  useEffect(() => {
    const cityId = userForm.city ? Number(userForm.city) : NaN;
    if (!userForm.city || Number.isNaN(cityId)) {
      setDistricts([]);
      return;
    }
    districtService.getAll({ cityId }).then((res) => {
      if (res.isSuccess && res.data) setDistricts(res.data);
    });
  }, [userForm.city]);

  const secondaryCategoriesForNavigator = useMemo(() => {
    if (!userForm.category) return [];
    const topId = Number(userForm.category);
    if (Number.isNaN(topId)) return [];
    const top = topCategories.find((t) => t.id === topId);
    return (top?.secondaryCategories ?? []).map((s) => ({ id: s.id, name: s.name }));
  }, [topCategories, userForm.category]);

  const productTypesForNavigator = useMemo(() => {
    if (!userForm.category || !userForm.secondaryCategoryId) return [];
    const topId = Number(userForm.category);
    const secId = Number(userForm.secondaryCategoryId);
    if (Number.isNaN(topId) || Number.isNaN(secId)) return [];
    const top = topCategories.find((t) => t.id === topId);
    const sec = top?.secondaryCategories?.find((s) => s.id === secId);
    return sec?.productTypes ?? [];
  }, [topCategories, userForm.category, userForm.secondaryCategoryId]);

  const categoryDraftForNavigator = useMemo<ListingFilterState>(
    () => ({
      ...defaultListingFilterState,
      topCategoryId: userForm.category ? Number(userForm.category) : null,
      secondaryCategoryId: userForm.secondaryCategoryId ? Number(userForm.secondaryCategoryId) : null,
      productTypeId: userForm.productTypeId ? Number(userForm.productTypeId) : null,
    }),
    [userForm.category, userForm.secondaryCategoryId, userForm.productTypeId]
  );

  const isComputerCategory = useMemo(() => {
    if (!userForm.category) return false;
    const topId = Number(userForm.category);
    const top = topCategories.find((t) => t.id === topId);
    return Boolean(top?.name?.toLowerCase().includes('bilgisayar'));
  }, [topCategories, userForm.category]);

  const setListingCategory = useCallback(
    (level: 'top' | 'secondary' | 'productType', id: number | null) => {
      if (level === 'top') {
        if (id == null) {
          setFormData((prev) => ({
            ...prev,
            category: '',
            secondaryCategoryId: '',
            productTypeId: '',
          }));
          setSpecConstraints([]);
          setSpecValues({});
          return;
        }
        const top = topCategories.find((t) => t.id === id);
        const secondaries = top?.secondaryCategories ?? [];
        setSpecConstraints([]);
        setSpecValues({});

        if (secondaries.length === 1) {
          const sec = secondaries[0];
          const types = sec.productTypes ?? [];
          if (types.length === 1) {
            const pt = types[0];
            setFormData((prev) => ({
              ...prev,
              category: String(id),
              secondaryCategoryId: String(sec.id),
              productTypeId: String(pt.id),
            }));
            void productTypeService.getSpecConstraints(pt.id).then((r) => {
              if (r.isSuccess && r.data) setSpecConstraints(normalizeSpecConstraintList(r.data));
              else setSpecConstraints([]);
            });
          } else {
            setFormData((prev) => ({
              ...prev,
              category: String(id),
              secondaryCategoryId: String(sec.id),
              productTypeId: '',
            }));
          }
        } else {
          setFormData((prev) => ({
            ...prev,
            category: String(id),
            secondaryCategoryId: '',
            productTypeId: '',
          }));
        }
        return;
      }

      if (level === 'secondary') {
        setSpecConstraints([]);
        setSpecValues({});
        setFormData((prev) => {
          const topId = prev.category ? Number(prev.category) : NaN;
          if (id == null || Number.isNaN(topId)) {
            return { ...prev, secondaryCategoryId: '', productTypeId: '' };
          }
          const top = topCategories.find((t) => t.id === topId);
          const secondary = top?.secondaryCategories?.find((s) => s.id === id);
          const types = secondary?.productTypes ?? [];
          if (types.length === 1) {
            const pt = types[0];
            void productTypeService.getSpecConstraints(pt.id).then((r) => {
              if (r.isSuccess && r.data) setSpecConstraints(normalizeSpecConstraintList(r.data));
              else setSpecConstraints([]);
            });
            return {
              ...prev,
              secondaryCategoryId: String(id),
              productTypeId: String(pt.id),
            };
          }
          return {
            ...prev,
            secondaryCategoryId: String(id),
            productTypeId: '',
          };
        });
        return;
      }

      setSpecConstraints([]);
      setSpecValues({});
      if (id == null) {
        setFormData((prev) => ({ ...prev, productTypeId: '' }));
        return;
      }
      setFormData((prev) => ({ ...prev, productTypeId: String(id) }));
      void productTypeService.getSpecConstraints(id).then((r) => {
        if (r.isSuccess && r.data) setSpecConstraints(normalizeSpecConstraintList(r.data));
        else setSpecConstraints([]);
      });
    },
    [topCategories]
  );

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setFormData(prev => ({ ...prev, city: id, district: '' }));
  };

  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, district: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!userForm.title || !userForm.price) {
      alert('Lütfen gerekli alanları doldurun');
      return;
    }

    if (!userForm.termsAccepted) {
      alert('Lütfen kullanım koşullarını kabul edin');
      return;
    }

    if (!userForm.category) {
      alert('Lütfen kategori seçin.');
      return;
    }

    const productTypeId = userForm.productTypeId;
    if (productTypesForNavigator.length > 0 && !productTypeId) {
      alert('Lütfen ürün tipini seçin.');
      return;
    }

    const city = userForm.city;
    const district = userForm.district;
    const brand = userForm.brand;
    const cpuModel = userForm.cpuModel;
    const specs: Record<string, string> = {
      ...(brand && { Marka: brand }),
      ...(cpuModel && { 'İşlemci Modeli': cpuModel }),
      ...(userForm.ram && { RAM: userForm.ram }),
      ...(userForm.storage && { Depolama: userForm.storage }),
      ...(userForm.gpu && { 'Ekran Kartı': userForm.gpu }),
    };

    const specsJson: Record<string, string> = {};
    Object.entries(specValues).forEach(([key, value]) => {
      const trimmedValue = value?.trim();
      if (trimmedValue) {
        specsJson[key] = trimmedValue;
      }
    });

    setSubmitting(true);
    try {
      const applicationForm = new FormData();
      applicationForm.append('Title', userForm.title);
      applicationForm.append('Description', userForm.description ?? '');
      applicationForm.append('Price', String(userForm.price));
      applicationForm.append('ConditionId', userForm.condition || '');
      applicationForm.append('ProductTypeId', productTypeId || '');
      applicationForm.append('CityId', city || '');
      applicationForm.append('DistrictId', district || '');
      const hasWarrantyFromApi = warranties.find((w) => String(w.id) === userForm.warranty)?.value === true;
      applicationForm.append('HasWarranty', (hasWarrantyFromApi ?? userForm.warranty === 'yes') ? 'true' : 'false');
      applicationForm.append('IsTested', userForm.testedSystem ? 'true' : 'false');
      applicationForm.append('ShowPhoneOnListing', userForm.showPhoneOnListing ? 'true' : 'false');
      if (Object.keys(specs).length > 0) {
        applicationForm.append('Specs', JSON.stringify(specs));
      }
      if (Object.keys(specsJson).length > 0) {
        applicationForm.append('SpecsJson', JSON.stringify(specsJson));
      }
      if (userForm.images?.length) {
        for (let i = 0; i < userForm.images.length; i++) {
          const file = userForm.images[i];
          if (file && isListingImageFile(file)) {
            applicationForm.append('Images', file);
          }
        }
      }

      if (reelFile) {
        applicationForm.append('Video', reelFile);
      }

      const result = await listingService.create(applicationForm);
      if (result.isSuccess) {
        setReelFile(null);
        alert('İlan başarıyla oluşturuldu!');
        navigate('/');
      } else {
        alert(result.message ?? 'İlan oluşturulamadı.');
      }
    } catch (err) {
      console.error('İlan oluşturma hatası:', err);
      alert('İlan oluşturulurken beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h2 className="card-title text-center mb-4">İlan Ver</h2>
              <form onSubmit={handleSubmit} className="needs-validation">
                {/* Filtreler Kartı */}
                <div className="card mb-4">
                  <div className="card-body">
                    <h5 className="card-title mb-3">İlan Özellikleri</h5>
                    
                    {/* Kategori Seçimi Section - Collapsible */}
                    <div className="property-section border-bottom pb-3 mb-3">
                      <button
                        type="button"
                        className="property-section-header w-100 d-flex justify-content-between align-items-center bg-transparent border-0 p-2"
                        onClick={() => togglePropertySection('categories')}
                      >
                        <h6 className="mb-0 fw-semibold">Kategori Seçimi</h6>
                        <i className={`fas fa-chevron-${propertySections.categories ? 'up' : 'down'} text-primary`} />
                      </button>
                      <div className={`collapse ${propertySections.categories ? 'show' : ''}`}>
                        <div className="category-section mb-2 mt-3">
                          <h6 className="fw-bold border-bottom pb-2 mb-3">Kategoriler</h6>
                          <SlidingCategoryNavigator
                            topCategories={topCategories}
                            secondaryCategories={secondaryCategoriesForNavigator}
                            productTypes={productTypesForNavigator}
                            draft={categoryDraftForNavigator}
                            setDraftCategory={setListingCategory}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Ürün Özellikleri Section - Collapsible */}
                    {specConstraints.length > 0 && (
                      <div className="property-section border-bottom pb-3 mb-3">
                        <button
                          type="button"
                          className="property-section-header w-100 d-flex justify-content-between align-items-center bg-transparent border-0 p-2"
                          onClick={() => togglePropertySection('specs')}
                        >
                          <h6 className="mb-0 fw-semibold">Ürün Özellikleri</h6>
                          <i className={`fas fa-chevron-${propertySections.specs ? 'up' : 'down'} text-primary`} />
                        </button>
                        <div className={`collapse ${propertySections.specs ? 'show' : ''}`}>
                          <div className="mb-3 mt-3">
                            <SpecInlineFilterPanel
                              constraints={specConstraints}
                              mode="create"
                              specValues={specValues}
                              specMultiValues={{}}
                              specOperators={{}}
                              onSpecValueChange={(key, value) =>
                                setSpecValues((prev) => ({ ...prev, [key]: value }))
                              }
                              onSpecMultiValueChange={() => {}}
                              onSpecOperatorChange={() => {}}
                              emptyMessage="Bu ürün tipi için tanımlı özellik yok."
                            />
                            <p className="text-muted small mt-2 mb-0">
                              Tüm özellikler opsiyoneldir. Sadece doldurduğunuz alanlar kaydedilecektir.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Ürün Durumu Section - Collapsible */}
                    <div className="property-section border-bottom pb-3 mb-3">
                      <button
                        type="button"
                        className="property-section-header w-100 d-flex justify-content-between align-items-center bg-transparent border-0 p-2"
                        onClick={() => togglePropertySection('condition')}
                      >
                        <h6 className="mb-0 fw-semibold">Ürün Durumu</h6>
                        <i className={`fas fa-chevron-${propertySections.condition ? 'up' : 'down'} text-primary`} />
                      </button>
                      <div className={`collapse ${propertySections.condition ? 'show' : ''}`}>
                        <div className="mb-3 mt-3">
                          <select
                            className="form-select"
                            name="condition"
                            value={userForm.condition}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Seçiniz</option>
                            {conditions.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name ?? `Durum ${c.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Garanti Durumu Section - Collapsible */}
                    <div className="property-section pb-3 mb-3">
                      <button
                        type="button"
                        className="property-section-header w-100 d-flex justify-content-between align-items-center bg-transparent border-0 p-2"
                        onClick={() => togglePropertySection('warranty')}
                      >
                        <h6 className="mb-0 fw-semibold">Garanti Durumu</h6>
                        <i className={`fas fa-chevron-${propertySections.warranty ? 'up' : 'down'} text-primary`} />
                      </button>
                      <div className={`collapse ${propertySections.warranty ? 'show' : ''}`}>
                        <div className="mb-3 mt-3">
                          <select
                            className="form-select"
                            name="warranty"
                            value={userForm.warranty}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Seçiniz</option>
                            {warranties.map((w) => (
                              <option key={w.id} value={w.id}>
                                {w.name ?? `Garanti ${w.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Başlık */}
                <div className="form-group mb-3">
                      <label className="form-label">İlan Başlığı</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={userForm.title}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Açıklama */}
                    <div className="form-group mb-3">
                      <label className="form-label">Açıklama</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={userForm.description}
                        onChange={handleChange}
                        rows={4}
                        required
                      />
                    </div>

                    {/* Adres Bilgileri */}
                    <div className="card mb-4 border-0 shadow-sm">
                      <div className="card-body p-4">
                        <h5 className="card-title mb-3 fw-bold">Adres Bilgileri</h5>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">İl</label>
                            <select
                              className="form-select"
                              name="city"
                              value={userForm.city}
                              onChange={handleCityChange}
                              required
                              aria-label="İl seçin"
                            >
                              <option value="">Seçiniz</option>
                              {cities.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name ?? `İl ${c.id}`}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">İlçe</label>
                            <select
                              className="form-select"
                              name="district"
                              value={userForm.district}
                              onChange={handleDistrictChange}
                              required
                              disabled={!userForm.city}
                              aria-label="İlçe seçin"
                            >
                              <option value="">Seçiniz</option>
                              {districts.map((d) => (
                                <option key={d.id} value={d.id}>
                                  {d.name ?? `İlçe ${d.id}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Birleştirilmiş Fotoğraf/Video ve Test Edilen Sistem Bölümü */}
                    <div className="card mb-4 border-0 shadow-sm">
                  <div className="card-body p-4">
                    {/* Fotoğraf ve Video Ekle */}
                    <div className="form-group mb-4">
                      <h5 className="card-title mb-3 fw-bold">Fotoğraf ve Video Ekle</h5>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleImageChange}
                        required
                      />
                      <small className="text-muted">En az 3, en fazla 10 fotoğraf ekleyebilirsiniz. Video eklemeniz tavsiye edilir.</small>
                    </div>

                    {/* Feature 009 (US2): optional reel tied to this listing. */}
                    <div className="form-group mb-4">
                      <h5 className="card-title mb-2 fw-bold">Reel Yükle (isteğe bağlı)</h5>
                      <ReelUpload
                        onSelect={(file) => {
                          setPendingReelFile(file);
                          setShowReelModal(true);
                        }}
                        disabled={submitting || showReelModal}
                      />
                      {reelFile && (
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <i className="fas fa-video text-success" aria-hidden="true" />
                          <small className="text-muted">{reelFile.name}</small>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            onClick={() => setReelFile(null)}
                          >
                            Kaldır
                          </button>
                        </div>
                      )}
                      <small className="text-muted d-block mt-1">
                        İsteğe bağlı. MP4 veya MOV, en fazla 50 MB.
                      </small>
                    </div>

                    <hr className="my-4" />

                    <h5 className="card-title mb-3 fw-bold">Telefon Görünürlüğü</h5>
                    <p className="card-text mb-3">
                      Profilinizde kayıtlı telefon numaranızın bu ilanda gösterilmesini isteyip istemediğinizi seçin.
                    </p>
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="showPhoneOnListing"
                        name="showPhoneOnListing"
                        checked={userForm.showPhoneOnListing}
                        onChange={handleCheckboxChange}
                      />
                      <label className="form-check-label" htmlFor="showPhoneOnListing">
                        Telefon numaramı ilanda göster
                      </label>
                    </div>

                    {/* Test Edilen Sistem Rozeti — only visible for computer categories */}
                    {isComputerCategory && <><h5 className="card-title mb-3 fw-bold">Test Edilen Sistem Rozeti Almak İstiyor musunuz?</h5>
                    <p className="card-text mb-3">
                      Test edilen sistem rozeti, bilgisayarınızın temel bileşenlerinin (işlemci, RAM, disk, ekran kartı) 
                      test edildiğini ve kararlı çalıştığını gösteren bir işarettir. Bu sayede ilanınız daha fazla güven uyandırır.
                    </p>
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="testedSystem"
                        name="testedSystem"
                        checked={userForm.testedSystem}
                        onChange={handleCheckboxChange}
                      />
                      <label className="form-check-label" htmlFor="testedSystem">
                        Evet, sistemimi test etmek ve rozet almak istiyorum
                      </label>
                    </div>
                    {/* Test Detayları Akordiyon */}
                    <div className="accordion mt-3" id="testDetailsAccordion">
                      <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                        <h2 className="accordion-header" id="headingOne">
                          <Button className="accordion-button collapsed sb-surface-muted" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="false" aria-controls="collapseOne">
                            <strong>1. İşlemci Testi</strong>
                          </Button>
                        </h2>
                        <div id="collapseOne" className="accordion-collapse collapse" aria-labelledby="headingOne" data-bs-parent="#testDetailsAccordion">
                          <div className="accordion-body">
                            <p>
                              İşlemcinin kararlılığını ve sıcaklığının görmek için CoreTemp ve Cpu-z programı kullanılır:
                            </p>
                            <ul>
                              <li>CoreTemp: İşlemci sıcaklığını gösterir</li>
                              <li>Cpu-z: İşlemcinin detaylı özelliklerini gösterir ve stress testi özelliği vardır</li>
                            </ul>
                            <p><strong>Test Adımları:</strong></p>
                            <ol>
                              <li>Cpu-z programında bench kısmı seçilir ve stress cpu butonuna basılır</li>
                              <li>Test bir kaç dakika çalıştırılır</li>
                              <li>Sıcaklık kontrolü yapılır (70-75 derece normal, 80-85 derece üzeri riskli)</li>
                            </ol>
                            <div className="alert alert-warning mt-2">
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              <strong>Not:</strong> İşlemciniz 80-85 dereceyi hızlı bir şekilde geçmeye başlarsa testi durdurunuz. 
                              İşlemci Termal macun değişimi gerekebilir. Bu test işlemcinize zarar vermemekte aksine yüksek sıcaklık 
                              kaynaklı oluşan işlemci ömrü ve performans düşüşü hakkında bize bilgi vermektedir.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                        <h2 className="accordion-header" id="headingTwo">
                          <Button className="accordion-button collapsed sb-surface-muted" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                            <strong>2. RAM Testi</strong>
                          </Button>
                        </h2>
                        <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#testDetailsAccordion">
                          <div className="accordion-body">
                            <p>
                              Techpowerup Memtest64 programı ile Windows üzerinde test yapılır:
                            </p>
                            <ol>
                              <li>Tested memory kısmından maximum seçilir</li>
                              <li>Stop after kısmına 2 yazılır</li>
                              <li>Begin test ile test başlatılır</li>
                              <li>Herhangi bir hata tespit edilmezse sağlamdır</li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item border-0 sb-surface-muted rounded mb-2">
                        <h2 className="accordion-header" id="headingThree">
                          <Button className="accordion-button collapsed sb-surface-muted" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="false" aria-controls="collapseThree">
                            <strong>3. HDD/SSD Sağlamlık Testi</strong>
                          </Button>
                        </h2>
                        <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#testDetailsAccordion">
                          <div className="accordion-body">
                            <p>
                              HDD Sentinel programı ile sistemdeki HDD ve SSD'lerin sağlık ve performans değerleri kontrol edilir.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item border-0 sb-surface-muted rounded">
                        <h2 className="accordion-header" id="headingFour">
                          <Button className="accordion-button collapsed sb-surface-muted" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour" aria-expanded="false" aria-controls="collapseFour">
                            <strong>4. Ekran Kartı Testi</strong>
                          </Button>
                        </h2>
                        <div id="collapseFour" className="accordion-collapse collapse" aria-labelledby="headingFour" data-bs-parent="#testDetailsAccordion">
                          <div className="accordion-body">
                            <p>
                              Geeks3D Furmark programı ile ekran kartı kararlılık testi yapılır:
                            </p>
                            <ol>
                              <li>Graphics test kısmından FurMark(GL) veya FurMark(VK) seçilir</li>
                              <li>Resolution seçeneğinden ekran çözünürlüğü ayarlanır</li>
                              <li>Run butonu ile test başlatılır (3-10 dakika arası) test yapılır daha sonra durdurulur</li>
                              <li>Test sırasında ekran da çizgiler ve kararma olmuyorsa ekran kartı kararlılığı iyi durumda diyebiliriz</li>
                            </ol>
                            <div className="alert alert-warning mt-2">
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              <strong>Not:</strong> Ekran kartı sıcaklığı 70-75 derece arası normal kabul edilir, 85 derece üzerine 
                              çıkması durumunda testi durdurunuz. Bilgisayarınızda oyunlarda takılma donma gibi sorunlar oluşuyorsa 
                              ekran kartı termal macun değişimi gerekebilir. Bu test doğru bir şekilde yapıldığında ekran kartına 
                              zarar vermemektedir.
                            </div>
                            <p className="mt-3">
                              En iyi test yöntemi, ekran kartı kullanımı yüksek oyunlarda birkaç saat test etmektir. 
                              Bu sayede sistemin genel kararlılığı hakkında da bilgi sahibi oluruz.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    </>}
                  </div>
                </div>

                {/* Fiyat - Buraya taşıyoruz */}
                <div className="form-group mb-3">
                  <label className="form-label">Fiyat (TL)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="price"
                    value={userForm.price}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                </div>

                {/* İlan Sözleşmesi */}
                <div className="form-check mb-4 mt-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="terms"
                    name="termsAccepted"
                    checked={userForm.termsAccepted}
                    onChange={handleCheckboxChange}
                    required
                  />
                  <label className="form-check-label" htmlFor="terms">
                    <a href="#" className="text-decoration-none" data-bs-toggle="modal" data-bs-target="#termsModal">
                      İlan Sözleşmesi</a>'ni okudum ve kabul ediyorum
                  </label>
                </div>

                {/* Gönder Butonu */}
                <div className="text-center">
                  <Button 
                    type="submit" 
                    className="btn btn-primary btn-lg w-100"
                    disabled={!userForm.termsAccepted || submitting}
                    loading={submitting}
                  >
                    İlanı Yayınla
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* İlan Sözleşmesi Modal */}
      <div className="modal fade" id="termsModal" tabIndex={-1} aria-labelledby="termsModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="termsModalLabel">İlan Sözleşmesi</h5>
              <Button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Kapat"></Button>
            </div>
            <div className="modal-body">
              <h6>SadeceBilgisayar.com İlan Kullanım Koşulları</h6>
              <p>
                Bu sözleşme, SadeceBilgisayar.com platformunda ilan veren kullanıcılar ile site yönetimi arasındaki hak ve yükümlülükleri belirler.
              </p>
              <h6>1. Genel Kurallar</h6>
              <p>
                - Kullanıcılar, platformda verdikleri ilanların içeriğinden tamamen kendileri sorumludur.<br/>
                - Telif hakkı ihlali içeren içerikler paylaşılamaz.<br/>
                - Yasadışı ürünlerin satışına yönelik ilanlar yayınlanamaz.<br/>
                - Her kullanıcı, kendi hesabını ve şifresini korumakla yükümlüdür.
              </p>
              <h6>2. İlan İçerikleri</h6>
              <p>
                - İlan başlıkları ve açıklamaları gerçeği yansıtmalıdır.<br/>
                - Ürün görselleri, satılan ürüne ait olmalıdır.<br/>
                - Fiyat bilgisi doğru ve güncel olmalıdır.
              </p>
              <h6>3. İlan Moderasyonu</h6>
              <p>
                SadeceBilgisayar.com, uygunsuz içerik barındıran ilanları kaldırma, düzenleme veya reddetme hakkını saklı tutar.
              </p>
              <p className="mb-0">
                <Link to="/ilan-sozlesmesi" target="_blank" rel="noopener noreferrer" className="text-primary">
                  Tam metni görüntüle
                </Link>
              </p>
            </div>
            <div className="modal-footer">
              <Button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Kapat</Button>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Property Section Collapse Styles - Feature 014: US3 */}
      <style>
        {`
        /* Property Section Collapse Styles - Matching AdFilter.tsx */
        .property-section {
          padding: 0.5rem 0;
        }

        .property-section-header {
          cursor: pointer;
          transition: background-color 200ms ease;
          border-radius: 4px;
        }

        .property-section-header:hover {
          background-color: var(--bs-light, #f8f9fa);
        }

        .property-section-header h6 {
          color: var(--bs-body-color);
          transition: color 200ms ease;
        }

        .property-section-header:hover h6 {
          color: var(--bs-primary, #0d6efd);
        }

        .property-section .collapse {
          transition: height 300ms ease-in-out;
        }

        .property-section-header i {
          transition: transform 200ms ease;
        }

        /* Ensure smooth expansion/collapse without layout shift */
        .property-section .collapse:not(.show) {
          display: none;
        }

        .property-section .collapse.show {
          display: block;
        }
        `}
      </style>

      <ReelUploadModal
        show={showReelModal}
        file={pendingReelFile}
        phase="preview"
        mode="attach"
        title="Reel ekle"
        confirmLabel="Ekle"
        onShare={() => {
          if (pendingReelFile) setReelFile(pendingReelFile);
          setShowReelModal(false);
          setPendingReelFile(null);
        }}
        onCancel={() => {
          setShowReelModal(false);
          setPendingReelFile(null);
        }}
        onRetry={() => {
          setShowReelModal(false);
          setPendingReelFile(null);
        }}
      />
    </>
  );
};

export default Ilan_ver;

